import type { UiNode, UiText } from '@ory/client';
import { NodeGroup } from './NodeGroup';
import { MessagesNode } from './UiNode';
type Props<T> = {
  ui: {
    messages?: UiText[];
    nodes: UiNode[];
    action?: string;
    method?: string;
  };
  disabled?: boolean;
  onSubmit?: (body: T) => void;
  /**
   * 表示するグループを指定
   * 指定されていない場合は全てのグループを表示
   * `default`グループは常に含まれる（CSRFトークンなど）
   */
  groups?: string[];
};

/**
 * グループの優先順位を定義
 * 数値が小さいほど先に表示される
 */
const GROUP_PRIORITY: Record<string, number> = {
  default: 0,
  // defaultは最初に表示
  password: 1,
  oidc: 2,
  webauthn: 3,
  profile: 4,
  totp: 5,
  code: 6,
  // メールMFA（コード送信）
  lookup_secret: 7
};

/**
 * グループの順序を決定する関数
 */
function getGroupOrder(groups: string[]): string[] {
  return [...groups].sort((a, b) => {
    const priorityA = GROUP_PRIORITY[a] ?? 100;
    const priorityB = GROUP_PRIORITY[b] ?? 100;
    return priorityA - priorityB;
  });
}

/*
 * FormDataからT型に変換
 */
function formDataToBody<T>(formData: FormData, submitter?: HTMLButtonElement | HTMLInputElement | null): T {
  const body: Record<string, unknown> = {};
  formData.forEach((value, key) => {
    if (key in body) {
      const existing = body[key];
      body[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
    } else {
      body[key] = value;
    }
  });
  if (submitter && submitter.name) {
    body[submitter.name] = submitter.value;
  }
  return body as unknown as T;
}

/**
 * Ory Kratos Formコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるUIオブジェクトから、完全なフォームを自動的に組み立てます。
 * ノードは`group`プロパティに基づいてグループ化され、優先順位に従って順序立ててレンダリングされます。
 *
 * グルーピングロジック:
 * - 各ノードの`group`プロパティに基づいてグループ化
 * - `group`が指定されていない場合は`default`グループとして扱う
 * - グループは優先順位に従ってソート（default → password → oidc → webauthn → ...）
 * - `default`グループは最初に表示される
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface
 */
export function OryForm<T extends object = Record<string, unknown>>({
  ui,
  disabled,
  onSubmit,
  groups
}: Props<T>) {
  // groupsが指定されている場合はフィルタリング
  // defaultグループは常に含める（CSRFトークンなど）
  const filteredNodes = groups ? ui.nodes.filter(node => {
    const group = node.group || 'default';
    return groups.includes(group) || group === 'default';
  }) : ui.nodes;

  // ノードをgroupでグループ化
  const groupedNodes = filteredNodes.reduce((acc, node) => {
    const group = node.group || 'default';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(node);
    return acc;
  }, {} as Record<string, UiNode[]>);

  // グループの順序を決定（優先順位に従ってソート）
  const groupOrder = getGroupOrder(Array.from(new Set(filteredNodes.map(node => node.group || 'default'))));
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // DOM処理はここで完結させる
    const form = e.currentTarget;
    // PasswordField の setCustomValidity 等、ネイティブ制約を尊重する。
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const formData = new FormData(form);

    // 押されたボタン(submitter)の情報を取得
    // nativeEvent.submitter は、フォーム送信をトリガーしたボタン要素を指します
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | HTMLInputElement | null;

    // 綺麗なオブジェクトに変換
    const body = formDataToBody<T>(formData, submitter);

    // 親には整形済みのデータを渡す
    onSubmit?.(body);
  };
  return <form action={ui.action} method={ui.method || 'POST'} onSubmit={handleSubmit} className="space-y-6">
      {/* ルートレベルのメッセージ */}
      {ui.messages && ui.messages.length > 0 && <MessagesNode messages={ui.messages} />}

      {/* グループごとにノードをレンダリング */}
      {groupOrder.map(group => {
      const nodes = groupedNodes[group];
      if (!nodes || nodes.length === 0) {
        return null;
      }
      return <NodeGroup key={group} group={group} nodes={nodes} disabled={disabled} />;
    })}
    </form>;
}